package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import com.islamicmedia.shared.domain.model.MediaCandidate
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

/** The media type's brand color as a CSS rgb() string (see MediaType.activeColorHex). */
private fun rgbParts(type: MediaType): Triple<Int, Int, Int> {
    val v = type.activeColorHex
    val r = (v shr 16 and 0xFFL).toInt()
    val g = (v shr 8 and 0xFFL).toInt()
    val b = (v and 0xFFL).toInt()
    return Triple(r, g, b)
}

private fun brandRgb(type: MediaType): String {
    val (r, g, b) = rgbParts(type)
    return "rgb($r, $g, $b)"
}

private fun brandTint(type: MediaType): String {
    val (r, g, b) = rgbParts(type)
    return "rgba($r, $g, $b, 0.14)"
}

/**
 * Modal shown when a photo contains more than one media item — the user picks which one they meant
 * instead of the app silently guessing. Web counterpart of the mobile ImageCandidatePicker: tinted
 * hero + eyebrow, then tappable brand-tinted rows.
 */
@Composable
fun ImageCandidatePicker(
    candidates: List<MediaCandidate>,
    onSelect: (MediaCandidate) -> Unit,
    onDismiss: () -> Unit
) {
    if (candidates.isEmpty()) return

    // Scrim
    Div(attrs = {
        onClick { onDismiss() }
        style {
            property("position", "fixed")
            property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
            property("background", "rgba(15, 23, 23, 0.5)")
            property("backdrop-filter", "blur(2px)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
            property("animation", "candFade 0.18s ease")
        }
    }) {
        // Card — stopPropagation so clicks inside don't dismiss
        Div(attrs = {
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Color.white)
                borderRadius(28.px)
                property("max-width", "400px")
                width(100.percent)
                property("box-shadow", "0 24px 70px rgba(14, 124, 123, 0.28), 0 4px 12px rgba(0,0,0,0.08)")
                property("box-sizing", "border-box")
                property("overflow", "hidden")
                property("animation", "candPop 0.22s cubic-bezier(0.4, 0, 0.2, 1)")
            }
        }) {
            // ── Hero: eyebrow + prompt on a soft tint ──
            Div(attrs = {
                style {
                    property("background", "linear-gradient(180deg, #E6F7F6 0%, #FFFFFF 100%)")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    padding(24.px, 24.px, 20.px)
                }
            }) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                        backgroundColor(Color.white)
                        borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                        padding(6.px, 12.px)
                        property("box-shadow", "0 1px 3px rgba(0,0,0,0.08)")
                    }
                }) {
                    Img(src = "/images/camera.png", attrs = {
                        style { width(12.px); height(12.px); display(DisplayStyle.Block) }
                    })
                    Span(attrs = {
                        style {
                            fontSize(10.px); fontWeight(700); color(Primary)
                            property("letter-spacing", "1px")
                        }
                    }) { Text("FROM YOUR PHOTO") }
                }
                Div(attrs = { style { fontSize(20.px); fontWeight(700); color(Gray900); marginTop(14.px); textAlign("center") } }) {
                    Text("Which one did you mean?")
                }
                Div(attrs = { style { fontSize(13.px); color(Gray500); marginTop(4.px); textAlign("center") } }) {
                    Text("We spotted a few titles in that photo.")
                }
            }

            // ── Candidate rows ──
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(10.px)
                    padding(18.px, 20.px, 6.px)
                }
            }) {
                candidates.forEach { candidate -> CandidateRow(candidate, onSelect) }
            }

            Div(attrs = {
                onClick { onDismiss() }
                style {
                    padding(14.px)
                    textAlign("center")
                    fontSize(14.px)
                    fontWeight(500)
                    color(Gray500)
                    cursor("pointer")
                }
            }) { Text("None of these") }
        }
    }

    InjectStyles(
        "image-candidate-picker-styles",
        """
        @keyframes candFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes candPop { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
        """.trimIndent()
    )
}

@Composable
private fun CandidateRow(candidate: MediaCandidate, onSelect: (MediaCandidate) -> Unit) {
    val type = candidate.mediaType
    val brand = type?.let { brandRgb(it) } ?: "#A3A3A3"
    val tint = type?.let { brandTint(it) } ?: "rgba(163,163,163,0.14)"

    Div(attrs = {
        onClick { onSelect(candidate) }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            padding(12.px)
            borderRadius(18.px)
            backgroundColor(PrimaryPale)
            property("border", "1px solid rgba(20, 163, 160, 0.2)")
            cursor("pointer")
            property("transition", TransitionFast)
        }
        onMouseOver {
            (it.currentTarget as? HTMLElement)?.style?.apply {
                setProperty("box-shadow", "0 6px 16px rgba(14,124,123,0.15)")
                setProperty("transform", "translateY(-1px)")
            }
        }
        onMouseOut {
            (it.currentTarget as? HTMLElement)?.style?.apply {
                setProperty("box-shadow", "none")
                setProperty("transform", "translateY(0)")
            }
        }
    }) {
        // Brand-tinted avatar with the media-type emoji (or first letter when type unknown)
        Div(attrs = {
            style {
                width(44.px); height(44.px)
                borderRadius(12.px)
                property("background", tint)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontSize(20.px)
                property("flex-shrink", "0")
                color(Color(brand))
                fontWeight(700)
            }
        }) {
            Text(type?.emoji ?: candidate.title.take(1).uppercase())
        }
        Div(attrs = { style { property("min-width", "0"); property("flex", "1") } }) {
            Div(attrs = {
                style {
                    fontSize(16.px); fontWeight(600); color(Gray900)
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }
            }) { Text(candidate.title) }
            type?.let {
                Div(attrs = { style { fontSize(13.px); fontWeight(500); color(Color(brand)); marginTop(2.px) } }) {
                    Text(it.singularLabel)
                }
            }
        }
        Span(attrs = { style { fontSize(22.px); color(Gray400); fontWeight(700); property("flex-shrink", "0") } }) {
            Text("›")
        }
    }
}
